import { forwardRef } from 'react';

import { compose, cva } from 'cva';
import {
  Text as RACText,
  type TextProps as RACTextProps,
} from 'react-aria-components/Text';

import styles from './text.module.css';

export const textColorVariants = cva({
  base: undefined,
  variants: {
    color: {
      inherit: styles.color_inherit,
      high_emphasis: styles.color_high_emphasis,
      medium_emphasis: styles.color_medium_emphasis,
      low_emphasis: styles.color_low_emphasis,
      primary: styles.color_primary,
      warning: styles.color_warning,
      error: styles.color_error,
      success: styles.color_success,
      primary_label: styles.color_primary_label,
    },
  },
  defaultVariants: {
    color: 'medium_emphasis',
  },
});

export const textOverflowVariants = cva({
  variants: {
    noWrap: {
      true: styles.no_wrap,
    },
  },
  defaultVariants: {
    noWrap: false,
  },
});

export const textVariantVariants = cva({
  base: styles.text,
  variants: {
    variant: {
      body: styles.variant_body,
      value: styles.variant_value,
      value_s: styles.variant_value_s,
      label: styles.variant_label,
      timecode: styles.variant_timecode,
      body_italic: styles.variant_body_italic,
      body_bold: styles.variant_body_bold,
    },
  },
  defaultVariants: {
    variant: 'body',
  },
});

export const textVariants = compose(
  textVariantVariants,
  textColorVariants,
  textOverflowVariants,
);

export type TextProps = RACTextProps & {
  elementType?: 'p' | 'span' | 'label' | string | undefined;
  variant?:
    | 'body'
    | 'value'
    | 'value_s'
    | 'label'
    | 'timecode'
    | 'body_italic'
    | 'body_bold';
  color?:
    | 'inherit'
    | 'high_emphasis'
    | 'medium_emphasis'
    | 'low_emphasis'
    | 'primary'
    | 'warning'
    | 'success'
    | 'error'
    | 'primary_label';
  noWrap?: boolean;
};

/**
 * Text - Represents text with no specific semantic meaning.
 *
 * ```tsx
 * <Text elementType="p" noWrap>Dolore veniam</Text>
 * ```
 */
export const Text = forwardRef<HTMLElement, TextProps>((props, ref) => {
  const {
    className,
    variant = 'body',
    noWrap = false,
    color,
    elementType,
    ...rest
  } = props;
  if (noWrap && !elementType) {
    throw new Error('[Text] `noWrap` is not supported on inline elements');
  }
  const classes = textVariants({ variant, className, noWrap, color });
  return (
    <RACText
      ref={ref}
      elementType={elementType}
      {...rest}
      className={classes}
    />
  );
});
